import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "custom-drag-layer"
}
const _hoisted_2 = {
  key: 0,
  class: "custom-drag-layer__card custom-drag-layer__card--shadow"
}
const _hoisted_3 = { class: "custom-drag-layer__card" }
const _hoisted_4 = ["src"]
const _hoisted_5 = { class: "custom-drag-layer__text" }
const _hoisted_6 = {
  key: 1,
  class: "custom-drag-layer__badge"
}
const _hoisted_7 = {
  key: 2,
  class: "custom-drag-layer__tooltip"
}

import { computed, onMounted, ref, unref, watch } from 'vue'
import { useDragLayer } from 'vue3-dnd'
import { BaseItemTypes } from './ItemTypes'

import selectionIconSvg from '@/assets/selection-icon.svg'
import projectionIconSvg from '@/assets/projection-icon.svg'
import listIconSvg from '@/assets/list-icon.svg'
import pieChartIconSvg from '@/assets/pie-chart-icon.svg'
import barChartIconSvg from '@/assets/bar-chart-icon.svg'
import lineChartIconSvg from '@/assets/line-chart-icon.svg'
import folderClosedSvg from '@/assets/folder-closed.svg'


export default /*@__PURE__*/_defineComponent({
  __name: 'CustomDragLayer',
  props: {
    selectedItems: {},
    flatItems: {},
    effectiveCount: {}
  },
  setup(__props: any) {

const props = __props

function makeSelectionKey(p: any): string {
  const elType = p.elType ?? ''
  const id = p.id ?? p.Id ?? ''
  const folderId = p.folderId ?? p.FolderId ?? ''
  const src = p.sourceDataType ?? p.SrcType ?? ''
  const dest = p.destinationDataType ?? p.DestType ?? ''
  // Согласовано с VirtualAnalyticsList/Analytics.makeElementLockKey: для дочерних элементов в отчётах
  // нужно различать конкретные ссылки (одна и та же сущность может встречаться в разных отчётах).
  const innerElementId = p.innerElementId ?? p.ElementId ?? ''
  return `${elType}:${id}:${folderId}:${src}:${dest}:${innerElementId}`
}

function iconForElType(elType: number | undefined): string {
  switch (elType) {
    case BaseItemTypes.SELECTION: return selectionIconSvg
    case BaseItemTypes.PROJECTION: return projectionIconSvg
    case BaseItemTypes.LIST: return listIconSvg
    case BaseItemTypes.PIE_CHART: return pieChartIconSvg
    case BaseItemTypes.BAR_CHART: return barChartIconSvg
    case BaseItemTypes.GRAPH: return lineChartIconSvg
    case BaseItemTypes.FOLDER: return folderClosedSvg
    default: return ''
  }
}

const layerState = useDragLayer((monitor) => ({
  isDragging: monitor.isDragging(),
  item: monitor.getItem(),
  currentOffset: monitor.getClientOffset(),
}))

// Страховка от залипшего превью: при сорванном жесте native dragend может прийти,
// а monitor.isDragging() ещё true — скрываем кастомный ghost до следующего drag.
const suppressAfterDragEnd = ref(false);

watch(() => unref(layerState).isDragging, (dragging) => {
  if (dragging) suppressAfterDragEnd.value = false;
});

onMounted(() => {
  document.addEventListener('dragend', () => {
    suppressAfterDragEnd.value = true;
  }, { capture: true });
});

const showCustomPreview = computed(() => {
  const state = unref(layerState)
  const cp = state.item?.callbackParams
  const dragType = cp?.elType
  const parentType = cp?.parent_type
  // Для reorder в settings-list оставляем только нативный browser ghost-preview.
  if (dragType === BaseItemTypes.SELECTED_COLUMN) return false
  // В режиме отчётов (и при reorder корневых отчётов, и при reorder детей внутри отчёта)
  // используем нативный ghost-preview как в SelectedListElement, без кастомного превью.
  if (dragType === BaseItemTypes.REPORT) return false
  if (parentType === BaseItemTypes.REPORT) return false
  return true
})

const selectedCount = computed(() => props.effectiveCount ?? props.selectedItems?.size ?? 0)

const resolvedItem = computed(() => {
  const state = unref(layerState)
  if (!state.isDragging || !state.item) return null

  const sel = props.selectedItems
  if (sel && sel.size > 0 && props.flatItems) {
    for (const fi of props.flatItems) {
      const key = makeSelectionKey(fi.item)
      if (sel.has(key)) {
        return { name: fi.item.Name ?? fi.item.CombinedName ?? '', elType: fi.item.elType }
      }
    }
  }

  return {
    name: state.item.elementName ?? '',
    elType: state.item.callbackParams?.elType,
  }
})

const displayName = computed(() => resolvedItem.value?.name ?? '')
const iconSrc = computed(() => iconForElType(resolvedItem.value?.elType))

const hasLockedProjection = computed(() => {
  const sel = props.selectedItems
  if (sel && sel.size > 0) {
    for (const item of sel.values()) {
      if (
        item.elType === BaseItemTypes.PROJECTION &&
        item.parent_type === BaseItemTypes.SELECTION
      ) return true
    }
  }
  const state = unref(layerState)
  const cp = state.item?.callbackParams
  if (cp && cp.elType === BaseItemTypes.PROJECTION && cp.parent_type === BaseItemTypes.SELECTION) {
    return true
  }
  return false
})

const cardStyle = computed(() => {
  const state = unref(layerState)
  if (!state.currentOffset) return { display: 'none' }
  return {
    position: 'absolute' as const,
    top: '0px',
    left: '0px',
    transform: `translate(${state.currentOffset.x}px, ${state.currentOffset.y}px)`,
    willChange: 'transform',
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (_unref(layerState).isDragging && showCustomPreview.value && !suppressAfterDragEnd.value && _unref(layerState).currentOffset)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", {
            style: _normalizeStyle(cardStyle.value),
            class: "custom-drag-layer__wrapper"
          }, [
            (selectedCount.value > 1)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_3, [
              (iconSrc.value)
                ? (_openBlock(), _createElementBlock("img", {
                    key: 0,
                    class: "custom-drag-layer__icon",
                    src: iconSrc.value
                  }, null, 8, _hoisted_4))
                : _createCommentVNode("", true),
              _createElementVNode("span", _hoisted_5, _toDisplayString(displayName.value), 1)
            ]),
            (selectedCount.value > 1)
              ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(selectedCount.value), 1))
              : _createCommentVNode("", true),
            (hasLockedProjection.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_ctx.$t('projection_move_with_selection')), 1))
              : _createCommentVNode("", true)
          ], 4)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})